import { useState, useEffect } from "react";
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Grid,
  Button,
  IconButton,
  Typography,
} from "@material-ui/core";
import CloseIcon from "@mui/icons-material/Close";
import InputText from "components/_refactor/text/input";
import AutoCompleteField from "components/_refactor/fields/autocomplete";
import DateField from "components/_refactor/fields/date";
import UploadFile from "components/_refactor/dropzone";
import useSnackbar from "components/Alerts/Snackbar/useSnackbar";
import themeColorsDesingSystem from "assets/theme/ds-colors";
import {
  CONTENT_STATUS,
  SEGMENTATION_OPTIONS,
  ACCEPT_IMAGES,
} from "../constants";
import {
  createContent,
  editContent,
  listCampaigns,
  buildFilePayload,
  buildSegmentacao,
  buildTags,
} from "../services/franchise-marketing-api";
import {
  populateContentForm,
  getAgendadoExtraFields,
} from "../utils/form-helpers";

const EMPTY_FORM = {
  nome: "",
  descricao: "",
  categoria: null,
  campanha: null,
  dataInicio: "",
  dataFim: "",
  status: null,
  segmentacao: null,
  ordemExibicao: "",
  tags: "",
  destaqueHome: false,
  dataPublicacaoAgendada: "",
};

export default function DialogBanner({
  open,
  onClose,
  onSaved,
  content,
  categoryOptions = [],
}) {
  const snackbar = useSnackbar();
  const [form, setForm] = useState(EMPTY_FORM);
  const [thumbnailFiles, setThumbnailFiles] = useState([]);
  const [bannerFiles, setBannerFiles] = useState([]);
  const [campanhaOptions, setCampanhaOptions] = useState([]);
  const [saving, setSaving] = useState(false);
  const fileResetBase = `${open}-${content?.IdFranquiaMarketingConteudo || content?.Id || "new"}`;

  const loadCampaignOptions = async () => {
    try {
      const campaigns = await listCampaigns();
      setCampanhaOptions(
        campaigns.map((c) => ({
          value: c.IdFranquiaMarketingCampanha,
          label: c.Nome,
        })),
      );
    } catch (error) {
      console.error(error);
    }
  };

  useEffect(() => {
    if (!open) return;
    loadCampaignOptions();
  }, [open]);

  useEffect(() => {
    if (!open) return;

    if (content) {
      const populated = populateContentForm(
        content,
        categoryOptions,
        campanhaOptions,
        [],
      );
      if (populated) setForm(populated);
    }
  }, [open, content, categoryOptions, campanhaOptions]);

  useEffect(() => {
    if (!open || content) return;
    setForm(EMPTY_FORM);
    setThumbnailFiles([]);
    setBannerFiles([]);
  }, [open, content]);

  const updateForm = (field, value) => {
    setForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSave = async () => {
    if (!form.nome?.trim()) {
      snackbar.showMessage("Nome do banner é obrigatório.", {
        severity: "error",
      });
      return;
    }

    if (!form.categoria?.value) {
      snackbar.showMessage("Categoria é obrigatória.", { severity: "error" });
      return;
    }

    setSaving(true);

    try {
      const thumbnailPayload = await buildFilePayload(thumbnailFiles, "thumbnail");
      const arquivoPayload = await buildFilePayload(bannerFiles, "arquivo");

      const payload = {
        titulo: form.nome.trim(),
        descricao: form.descricao,
        tipo: "Banner",
        idFranquiaMarketingCategoria: form.categoria.value,
        idFranquiaMarketingCampanha: form.campanha?.value || null,
        dataInicioVigencia: form.dataInicio || null,
        dataFimVigencia: form.dataFim || null,
        status: form.status?.value || "Rascunho",
        ordemExibicao: Number(form.ordemExibicao) || 0,
        destaqueHome: form.destaqueHome,
        tags: buildTags(form.tags),
        segmentacao: buildSegmentacao(form.segmentacao),
        ...getAgendadoExtraFields(form),
        ...thumbnailPayload,
        ...arquivoPayload,
      };

      const editId =
        content?.IdFranquiaMarketingConteudo || content?.id || content?.Id;

      if (editId) {
        await editContent({
          ...payload,
          idFranquiaMarketingConteudo: editId,
        });
        snackbar.showMessage("Banner atualizado com sucesso.", {
          severity: "success",
        });
      } else {
        await createContent(payload);
        snackbar.showMessage("Banner salvo com sucesso.", { severity: "success" });
      }

      onSaved?.();
    } catch (error) {
      console.error(error);
      snackbar.showMessage(
        error?.message || "Falha ao salvar banner.",
        { severity: "error" },
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      scroll="paper"
    >
      <DialogTitle>
        <Typography
          variant="h4"
          component="span"
          style={{ color: themeColorsDesingSystem.text.primary }}
        >
          {content ? "Editar Banner" : "Cadastro de Banner"}
        </Typography>
        <IconButton
          onClick={onClose}
          style={{ position: "absolute", right: 8, top: 8 }}
          aria-label="Fechar"
        >
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Grid container spacing={3}>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <InputText
                  label="Nome do banner"
                  value={form.nome}
                  onChange={(e) => updateForm("nome", e.target.value)}
                  required
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Descrição"
                  value={form.descricao}
                  onChange={(e) => updateForm("descricao", e.target.value)}
                  multiline
                  rows={2}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Categoria"
                  value={form.categoria}
                  onChange={(v) => updateForm("categoria", v)}
                  options={categoryOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Vigência início"
                  value={form.dataInicio}
                  onChange={(v) => updateForm("dataInicio", v)}
                />
              </Grid>
              <Grid item xs={6}>
                <DateField
                  label="Vigência fim"
                  value={form.dataFim}
                  onChange={(v) => updateForm("dataFim", v)}
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Status"
                  value={form.status}
                  onChange={(v) => updateForm("status", v)}
                  options={CONTENT_STATUS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Campanha vinculada"
                  value={form.campanha}
                  onChange={(v) => updateForm("campanha", v)}
                  options={campanhaOptions}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <AutoCompleteField
                  label="Segmentação"
                  value={form.segmentacao}
                  onChange={(v) => updateForm("segmentacao", v)}
                  options={SEGMENTATION_OPTIONS}
                  optionLabelKey="label"
                  optionIdKey="value"
                />
              </Grid>
              <Grid item xs={12}>
                <InputText
                  label="Tags"
                  value={form.tags}
                  onChange={(e) => updateForm("tags", e.target.value)}
                  placeholder="Ex: inverno, coleção, promoção"
                />
              </Grid>
              {form.status?.value === "Agendado" && (
                <Grid item xs={12}>
                  <InputText
                    label="Data/hora de publicação agendada"
                    type="datetime-local"
                    value={form.dataPublicacaoAgendada}
                    onChange={(e) =>
                      updateForm("dataPublicacaoAgendada", e.target.value)
                    }
                    InputLabelProps={{ shrink: true }}
                  />
                </Grid>
              )}
              <Grid item xs={12}>
                <InputText
                  label="Ordem de exibição"
                  type="number"
                  value={form.ordemExibicao}
                  onChange={(e) => updateForm("ordemExibicao", e.target.value)}
                />
              </Grid>
            </Grid>
          </Grid>
          <Grid item xs={12} md={6}>
            <Grid container spacing={2}>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Thumbnail
                </Typography>
                <UploadFile
                  message="Arraste a thumbnail ou clique para selecionar"
                  accept={ACCEPT_IMAGES}
                  onChangeFiles={setThumbnailFiles}
                  resetKey={`${fileResetBase}-thumbnail`}
                />
              </Grid>
              <Grid item xs={12}>
                <Typography
                  variant="caption"
                  style={{ color: themeColorsDesingSystem.gray.dark }}
                >
                  Banner principal
                </Typography>
                <UploadFile
                  message="Arraste o banner ou clique para selecionar"
                  accept={ACCEPT_IMAGES}
                  onChangeFiles={setBannerFiles}
                  resetKey={`${fileResetBase}-banner`}
                />
              </Grid>
            </Grid>
          </Grid>
        </Grid>
      </DialogContent>
      <DialogActions style={{ padding: "16px 24px" }}>
        <Button onClick={onClose} variant="outlined" disabled={saving}>
          Cancelar
        </Button>
        <Button
          onClick={handleSave}
          variant="contained"
          color="primary"
          disabled={saving}
        >
          Salvar banner
        </Button>
      </DialogActions>
    </Dialog>
  );
}
